import React, { Component } from 'react';
import styled from 'styled-components';
import { changeLocation } from 'api/Location';

import Auth from '../../../routes/Auth';
import {
  postRequest,
  patchRequest,
} from '../../../api/ApiService';
import { clearAppGeneralInfoCache } from '../../../api/AppGeneralInfoService';

import OnboardingLayout from './OnboardingLayout';
import { getNextOnboardingStep } from './onboardingRouting';

const API = process.env.REACT_APP_API_URL;

// Hardcoded round-target defaults seeded during onboarding, keyed by
// round number. Percentages are stored as fractions (0.15 = 15%);
// revenues are raw dollars.
const ROUND_TARGETS = {
  1: {
    market_share: 0.15,
    net_revenues: 4000000,
    gross_margin: 0.60,
    stock_price: 1.65,
    roe: 0,
    roa: 0,
    roic: 0,
    earnings_per_share: 0,
  },
  2: {
    market_share: 0.15,
    net_revenues: 10000000,
    gross_margin: 0.55,
    stock_price: 3,
    roe: 0,
    roa: 0,
    roic: 0,
    earnings_per_share: 0,
  },
  3: {
    market_share: 0.15,
    net_revenues: 17500000,
    gross_margin: 0.55,
    stock_price: 0,
    roe: 0,
    roa: 0,
    roic: 0,
    earnings_per_share: 0.25,
  },
};

const Heading = styled.h1`
  margin: 16px 0 4px;
  font-family: 'Mukta Vaani', 'Mukta Mahee', sans-serif;
  font-size: 24px;
  line-height: 60px;
  font-weight: 700;
  color: #2F4166;
  text-align: center;
`;

const Subheading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 32px;
  font-family: 'Mukta Vaani', 'Mukta Mahee', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #2F4166;
`;

const HelpIconWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const HelpIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1.2px solid #2F4166;
  font-size: 11px;
  color: #2F4166;
  font-weight: 700;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:hover {
    background: rgba(47, 65, 102, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }
`;

const HelpPopover = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  background: #FFFFFF;
  border: 1px solid rgba(47, 65, 102, 0.15);
  border-radius: 10px;
  padding: 16px 18px 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
  z-index: 20;
  text-align: left;
  font-family: 'Mukta Vaani', 'Mukta Mahee', sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #FFFFFF;
    border-top: 1px solid rgba(47, 65, 102, 0.15);
    border-left: 1px solid rgba(47, 65, 102, 0.15);
  }

  ul {
    margin: 8px 0 10px;
    padding-left: 18px;
  }

  li {
    margin-bottom: 4px;
  }

  strong {
    font-weight: 700;
  }
`;

const HelpPopoverClose = styled.button`
  position: absolute;
  top: 6px;
  right: 8px;
  background: transparent;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: rgba(0, 27, 59, 0.5);
  cursor: pointer;
  padding: 4px 6px;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  min-height: 100px;
  background: #FFFFFF;
  border: ${p => p.selected ? '2px solid #2F4166' : '2px solid transparent'};
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  box-shadow: ${p => p.selected ? '0 4px 12px rgba(47, 65, 102, 0.15)' : 'none'};

  &:hover { border-color: ${p => p.selected ? '#2F4166' : 'rgba(47, 65, 102, 0.3)'}; }
`;

const OptionTag = styled.div`
  flex: 0 0 auto;
  min-width: 105px;
  height: 40px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #D5D9DD;
  border-radius: 5px;
  background: #FFFFFF;
  font-family: 'Mukta Vaani', 'Mukta Mahee', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #2F4166;
`;

const OptionText = styled.div`
  flex: 1;
  font-family: 'Mukta Vaani', 'Mukta Mahee', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);

  strong { font-weight: 700; }
`;

const OPTIONS = [
  {
    id: 'Explorer',
    tag: 'Explorer',
    text: (
      <span>
        Students complete <strong>Round 1 only</strong>: practice once, then redo.
      </span>
    ),
  },
  {
    id: 'Adventurer',
    tag: 'Adventurer',
    text: (
      <span>
        <strong>Students complete all 3 rounds. They start with practice,</strong>
        {' '}and then tackle the full challenge from capturing market share to
        generating profits.
      </span>
    ),
  },
  {
    id: 'Legend',
    tag: 'Legend',
    text: (
      <span>
        Students jump straight into the full challenge with{' '}
        <strong>no practice round.</strong>
      </span>
    ),
  },
];

class SimulationSelection extends Component {
  state = { selected: null, submitting: false, helpOpen: false };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleDocumentClick = (e) => {
    if (!this.state.helpOpen) return;
    if (this.helpWrapRef && this.helpWrapRef.contains(e.target)) return;
    this.setState({ helpOpen: false });
  };

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && this.state.helpOpen) {
      this.setState({ helpOpen: false });
    }
  };

  toggleHelp = (e) => {
    e.stopPropagation();
    this.setState(prev => ({ helpOpen: !prev.helpOpen }));
  };

  setHelpWrapRef = (el) => {
    this.helpWrapRef = el;
  };

  handleSelect = (id) => {
    if (this.state.submitting) return;
    this.setState({ selected: id });
  };

  handleContinue = () => {
    const { selected, submitting } = this.state;
    if (!selected || submitting) return;

    this.setState({ submitting: true });

    this.updateCourseGameType(selected)
      .then(() => this.createGame(selected))
      .then(() => this.createRoundTargets(selected))
      .then(() => {
        // Route via utility so we skip team-evaluation-survey if the
        // course already has a survey (partial-onboarding case). Game
        // now exists (we just created it) so pass hasGame=true.
        const setup = this.props.onboardingSetup || { hasGame: true, hasSurvey: false };
        const user = Auth.getUser();
        const onboardComplete = !!(user && user.onboard !== 0);
        changeLocation(getNextOnboardingStep('simulation-selection', {
          hasGame: true,
          hasSurvey: setup.hasSurvey,
          onboardComplete,
        }));
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  createRoundTargets = (gameType) => {
    const courseId = Number(Auth.getCourseId());
    // Explorer plays only Round 1; Adventurer and Legend play all three.
    const roundNumbers = gameType === 'Explorer' ? [1] : [1, 2, 3];

    const requests = roundNumbers.map(n =>
      postRequest(`${API}v2/games/targets/?round_number=${n}/`)
        .send({
          course: courseId,
          round_number: n,
          ...ROUND_TARGETS[n],
        })
        // Allow partial success: instructor can complete missing rows
        // later on the Set Team Goals & Targets page.
        .catch(() => null)
    );

    return Promise.all(requests);
  };

  updateCourseGameType = (gameType) => {
    const courseId = Number(Auth.getCourseId());

    return new Promise((resolve, reject) => {
      patchRequest(`${API}courses/${courseId}/`)
        .send({ game_type: gameType })
        .then(() => resolve())
        .catch(err => reject(err));
    });
  };

  createGame = (gameType) => {
    const courseId = Number(Auth.getCourseId());
    const reset = gameType === 'Legend';

    return new Promise((resolve, reject) => {
      postRequest(`${API}games/`)
        .send({
          name: 'The Intro to Business Challenge',
          course: courseId,
          reset,
        })
        .then(res => {
          if (res.body && res.body.id) {
            Auth.setGameId(res.body.id);
          }
          // Bust the 5-min AppGeneralInfo cache so the next
          // fetchAppGeneralInfo (the onboarding gate in App.js re-runs
          // it on the very next navigation) sees the game we just
          // created. Without this, hasGame stays false, the gate
          // routes the instructor back to /instructor/onboarding/intro
          // right after they click Continue on team-evaluation-survey.
          clearAppGeneralInfoCache();
          resolve();
        })
        .catch(err => reject(err));
    });
  };

  render() {
    const { selected, submitting } = this.state;

    return (
      <OnboardingLayout
        progressStep={1}
        progressTotal={4}
        buttonLabel={submitting ? 'Saving' : 'Continue'}
        buttonDisabled={!selected || submitting}
        onButtonClick={this.handleContinue}
      >
        <Heading>Select Simulation Experience</Heading>
        <Subheading>
          Time Commitment
          <HelpIconWrap innerRef={this.setHelpWrapRef}>
            <HelpIcon
              type="button"
              onClick={this.toggleHelp}
              aria-label="About time commitment"
              aria-expanded={this.state.helpOpen}
            >
              ?
            </HelpIcon>
            {this.state.helpOpen && (
              <HelpPopover role="dialog" aria-label="Time commitment details">
                <HelpPopoverClose
                  type="button"
                  onClick={() => this.setState({ helpOpen: false })}
                  aria-label="Close"
                >
                  ×
                </HelpPopoverClose>
                Plan for how much time students need each round:
                <ul>
                  <li><strong>Round 1:</strong> 2 to 3 hours. Students are new to the simulation and getting comfortable.</li>
                  <li><strong>Rounds 2 and 3:</strong> 1 to 1.5 hours once they're up to speed.</li>
                </ul>
                We recommend <strong>1 week per round</strong>. You can pick shorter or longer, up to <strong>30 days</strong> max.
              </HelpPopover>
            )}
          </HelpIconWrap>
        </Subheading>
        {OPTIONS.map(opt => (
          <OptionRow
            key={opt.id}
            selected={selected === opt.id}
            onClick={() => this.handleSelect(opt.id)}
          >
            <OptionTag>{opt.tag}</OptionTag>
            <OptionText>{opt.text}</OptionText>
          </OptionRow>
        ))}
      </OnboardingLayout>
    );
  }
}

export default SimulationSelection;



// WEBPACK FOOTER //
// ./src/instructor/components/onboarding/SimulationSelection.js